import { useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { toast } from 'sonner';

import { useCreateCampaign } from '@/entities/campaign';
import { Button } from '@/shared/components/shadcn/button';
import { Tabs, TabsContent } from '@/shared/components/shadcn/tabs';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { cn } from '@/shared/utils';
import { ArrowLeft, ArrowRight, Link2, UserPlus2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  getCampaignEditorCreatorIssues,
  getCampaignEditorSubmitValidationIssues,
} from '../../campaign-editor/helpers/campaign-editor-validation';
import { getFooterSecondaryAction } from '../../campaign-editor/helpers/footer-secondary-action';
import { useCampaignEditorDraft } from '../../campaign-editor/hooks/use-campaign-editor-draft';
import { CampaignEditorPhaseTabs } from '../../campaign-editor/primitives';
import { campaignInfoIssueAtom } from '../../campaign-editor/store/campaign-editor-issues';
import { CampaignEditorProvider } from '../../campaign-editor/store/campaign-editor-provider';
import type { CampaignEditorSection } from '../../campaign-editor/types';
import { CampaignBasicsSection } from '../../campaign-editor/ui/basic-section/campaign-basics.section';
import { CampaignEditorFooter } from '../../campaign-editor/ui/campaign-editor-footer';
import { CampaignCreatorsSection } from '../../campaign-editor/ui/creator-section/campaign-creators.section';
import { CampaignPostsSection } from '../../campaign-editor/ui/post-section/campaign-posts.section';
import { buildCampaignCreatePayload } from './helpers/build-campaign-create-payload';
import {
  getCampaignCreateFooterViewModel,
  getCampaignCreateSectionForValidationIssue,
} from './helpers/campaign-create-page-view-model';
import { SupportedPlatformBadge } from '../supported-platform-badge';

function getSetupRequirementTooltip({
  campaignName,
  campaignStartDate,
  campaignEndDate,
  hasEndDate,
}: {
  campaignName: string;
  campaignStartDate: string;
  campaignEndDate?: string;
  hasEndDate: boolean;
}) {
  const missingFields = [
    !campaignName.trim() ? 'campaign name' : null,
    !campaignStartDate ? 'start date' : null,
    hasEndDate && !campaignEndDate ? 'end date' : null,
  ].filter((field): field is string => Boolean(field));

  if (missingFields.length === 0) {
    return 'Complete the required setup fields first.';
  }

  if (missingFields.length === 1) {
    return `Fill in the ${missingFields[0]} first.`;
  }

  if (missingFields.length === 2) {
    return `Fill in the ${missingFields[0]} and ${missingFields[1]} first.`;
  }

  return `Fill in ${missingFields.slice(0, -1).join(', ')}, and ${missingFields.at(-1)} first.`;
}

export function CampaignCreatePage() {
  return (
    <CampaignEditorProvider>
      <CampaignCreatePageContent />
    </CampaignEditorProvider>
  );
}

function CampaignCreatePageContent() {
  const navigate = useNavigate();
  const createCampaignMutation = useCreateCampaign();
  const { workspace } = useWorkspace();
  const campaignItemSlotLimit = workspace.constraints.campaign_item_slot_limit;
  const draft = useCampaignEditorDraft(campaignItemSlotLimit);
  const campaignInfoIssue = useAtomValue(campaignInfoIssueAtom);
  const [activeSection, setActiveSection] = useState<CampaignEditorSection>('campaign-info');
  const [creatorsValidationRequest, setCreatorsValidationRequest] = useState(0);
  const fieldPolicy = {
    startDateEditable: true,
  } as const;
  const payload = draft.serialize();
  const creatorIssues = getCampaignEditorCreatorIssues(payload.creatorItems);
  const validationIssues = getCampaignEditorSubmitValidationIssues({
    campaignItemSlotLimit,
    payload,
    startDateEditable: fieldPolicy.startDateEditable,
    workspaceTimezone: workspace.timezone,
  });
  const setupValid = !campaignInfoIssue;
  const isSubmitting = createCampaignMutation.isPending;
  const footer = getCampaignCreateFooterViewModel({
    campaignItemSlotLimit,
    isSubmitting,
    totalTrackedItems: draft.summary.totalTrackedItems,
    validationIssue: validationIssues[0],
    validationIssueCount: validationIssues.length,
  });
  const setupRequirementTooltip = getSetupRequirementTooltip({
    campaignName: draft.basics.campaignName,
    campaignStartDate: draft.basics.campaignStartDate,
    campaignEndDate: draft.basics.campaignEndDate,
    hasEndDate: draft.basics.hasEndDate,
  });

  const footerSecondaryAction = getFooterSecondaryAction([activeSection, setActiveSection]);

  const submit = async () => {
    if (creatorIssues.length > 0) {
      setCreatorsValidationRequest((count) => count + 1);
    }

    if (validationIssues.length > 0 || isSubmitting) {
      if (validationIssues[0]) {
        setActiveSection(
          getCampaignCreateSectionForValidationIssue({
            currentSection: activeSection,
            issue: validationIssues[0],
          }),
        );
      }
      return;
    }

    await createCampaignMutation.mutateAsync(buildCampaignCreatePayload(draft.serialize()));
    toast.success('Campaign created.');
    navigate({ to: '/workspace/$workspaceId/campaigns/' });
  };

  return (
    <Tabs
      value={activeSection}
      onValueChange={(value) => setActiveSection(value as CampaignEditorSection)}
      className="bg-background flex h-full min-h-0 flex-col gap-0"
    >
      <header className="bg-background -translate-x-3 px-6 py-3">
        <div className="mx-auto flex w-full max-w-[760px] items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/workspace/$workspaceId/campaigns/' })}
              className="size-10"
            >
              <ArrowLeft />
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-foreground text-left text-2xl font-bold">Create a campaign</h1>
              <SupportedPlatformBadge />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto px-6 pb-32">
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-10 py-4">
          <CampaignEditorPhaseTabs setupReady={setupValid} />

          <TabsContent value="campaign-info" className="mt-0 space-y-12">
            <section>
              <CampaignBasicsSection
                draft={draft.basics}
                startDateEditable={fieldPolicy.startDateEditable}
              />
            </section>

            <ToggleStage status={setupValid}>
              <section className="space-y-3">
                <div className="space-y-1">
                  <h2 className="text-foreground text-base font-semibold tracking-tight">
                    Next step
                  </h2>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <NextActionCard
                    tooltipContent={setupRequirementTooltip}
                    onClick={() => setActiveSection('creators')}
                    icon={<UserPlus2 className="size-4" />}
                    title="Add Creator"
                    description="Begin with creator candidates first and collect their posts as you refine the list."
                  />
                  <NextActionCard
                    tooltipContent={setupRequirementTooltip}
                    onClick={() => setActiveSection('post-urls')}
                    icon={<Link2 className="size-4" />}
                    title="Add Post URL"
                    description="Start with post links when you already know the content you want to track."
                  />
                </div>
              </section>
            </ToggleStage>
          </TabsContent>

          <TabsContent value="creators" className="mt-0">
            <section>
              <CampaignCreatorsSection
                draft={draft.creators}
                campaignItemSlotLimit={campaignItemSlotLimit}
                remainingSlots={draft.summary.remainingSlots}
                validationRequest={creatorsValidationRequest}
              />
            </section>
          </TabsContent>

          <TabsContent value="post-urls" className="mt-0">
            <section>
              <CampaignPostsSection
                draft={draft.posts}
                campaignItemSlotLimit={campaignItemSlotLimit}
                remainingSlots={draft.summary.remainingSlots}
              />
            </section>
          </TabsContent>
        </div>
      </main>

      <footer className="bg-background/95 sticky bottom-0 z-20 border-t px-6 py-4 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[760px] items-center justify-between gap-4">
          <CampaignEditorFooter
            message={footer.message}
            usageLabel={footer.usageLabel}
            secondaryAction={footerSecondaryAction}
            primaryAction={{
              label: footer.primaryLabel,
              disabled: footer.primaryDisabled,
              onClick: () => void submit(),
            }}
          />
        </div>
      </footer>
    </Tabs>
  );
}

const NextActionCard = ({
  onClick,
  tooltipContent,
  description,
  title,
  icon,
}: {
  tooltipContent: string;
  onClick: () => void;
  title: string;
  description: string;
  icon: ReactNode;
}) => {
  return (
    <TooltipFlat
      asChild
      side="top"
      content={tooltipContent}
      contentClassName={cn('max-w-[260px] text-sm leading-5 hidden')}
    >
      <span className="block">
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'group bg-primary text-primary-foreground border-border w-full cursor-pointer rounded-2xl border p-4 text-left transition-colors',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="bg-background text-foreground inline-flex size-10 shrink-0 items-center justify-center rounded-xl">
                {icon}
              </span>
              <div className="space-y-1">
                <div className="text-sm font-semibold">{title}</div>
                <p className="text-primary-foreground/70 text-sm leading-5">{description}</p>
              </div>
            </div>
            <ArrowRight className="mt-0.5 size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
          </div>
        </button>
      </span>
    </TooltipFlat>
  );
};
